import React, { useRef } from "react";
import moment from "moment";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import styles from "./DateField.module.css";

/**
 * The batch flow's date control: the value shown as DD-MM-YYYY with a calendar
 * icon, over a transparent native date input.
 *
 * The native control holds the value and owns the calendar, but a browser opens
 * that calendar only from its own indicator — a few pixels at the field's right
 * edge. The control here is transparent and covers the whole field, so a click
 * anywhere else landed on an invisible date segment and nothing opened. The
 * indicator is hidden in the stylesheet and the field asks for the calendar
 * itself, wherever it is clicked.
 *
 * `invalid` outlines the field in red while a message under it says what is wrong.
 *
 * @param {{ value?: string, min?: string, disabled?: boolean, invalid?: boolean,
 *           ariaLabel?: string, onChange?: (value: string) => void }} props - dates as "YYYY-MM-DD"
 */
const DateField = ({ value, min, disabled, invalid, ariaLabel = "Date", onChange }) => {
  const inputRef = useRef(null);

  const openPicker = () => {
    const input = inputRef?.current;
    input?.focus();
    input?.showPicker?.();
  };

  return (
    <span className={disabled ? styles.shellLocked : styles.shell}>
      <span className={invalid ? styles.valueInvalid : styles.value}>
        {value ? (
          moment(value, "YYYY-MM-DD").format("DD-MM-YYYY")
        ) : (
          <span className={styles.placeholder}>Select date</span>
        )}
      </span>
      <input
        ref={inputRef}
        type="date"
        className={styles.native}
        aria-label={ariaLabel}
        aria-invalid={!!invalid}
        value={value ?? ""}
        min={min}
        disabled={disabled}
        onClick={openPicker}
        onChange={(event) => onChange?.(event?.target?.value)}
      />
      <SvgIcons name="calendar_icon" className={styles.icon} width="20" height="20" />
    </span>
  );
};

export default DateField;
